/**
 * Column Manager - searchable tree for show/hide, pin, reorder, and collapse operations.
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Button,
  Input,
  Checkbox,
  Icon,
  useTheme,
  spacing,
  fontSize,
  fontFamily,
} from '@fti/design-system';
import type { ColumnManagerProps } from './contracts';
import type { ColumnDef, ColumnGroup } from './model/types';
import { TYPE_BADGE } from './format';

interface TreeEntry {
  kind: 'column' | 'group';
  id: string;
  name: string;
  level: number;
  column?: ColumnDef;
  group?: ColumnGroup;
}

export function ColumnManager({ schema, layout, summaries, onChange, onJumpTo }: ColumnManagerProps) {
  const { theme } = useTheme();
  const [searchText, setSearchText] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Compute visible and pinned counts
  const visibleCount = useMemo(() => {
    return schema.columns.filter((col) => !layout.hidden.includes(col.id)).length;
  }, [schema.columns, layout.hidden]);

  const pinnedCount = layout.pinned.length;

  // Build tree entries in first-seen order
  const treeEntries = useMemo(() => {
    if (searchText.trim()) {
      // Flat list of matching columns
      const lowerSearch = searchText.toLowerCase();
      return schema.columns
        .filter((col) => col.id.toLowerCase().includes(lowerSearch))
        .map(
          (col): TreeEntry => ({
            kind: 'column',
            id: col.id,
            name: col.id,
            level: 0,
            column: col,
          })
        );
    }

    // Build tree: top-level columns and groups in first-seen order
    const entries: TreeEntry[] = [];
    const processedGroups = new Set<string>();

    // Collect top-level items by first appearance in schema.columns
    const topLevelItems: Array<{ kind: 'column' | 'group'; id: string; firstSeen: number }> = [];

    // Top-level columns
    schema.columns.forEach((col) => {
      if (col.group === null) {
        topLevelItems.push({ kind: 'column', id: col.id, firstSeen: col.firstSeen });
      }
    });

    // Top-level groups by their first column's firstSeen
    schema.groups.forEach((group) => {
      if (group.parent === null && group.columnIds.length > 0) {
        const firstCol = schema.byId.get(group.columnIds[0]);
        if (firstCol) {
          topLevelItems.push({ kind: 'group', id: group.id, firstSeen: firstCol.firstSeen });
        }
      }
    });

    // Sort by firstSeen
    topLevelItems.sort((a, b) => a.firstSeen - b.firstSeen);

    // Build entries recursively
    const addGroupEntries = (groupId: string, level: number) => {
      const group = schema.groupById.get(groupId);
      if (!group || processedGroups.has(groupId)) return;
      processedGroups.add(groupId);

      entries.push({
        kind: 'group',
        id: groupId,
        name: group.name,
        level,
        group,
      });

      if (expandedGroups.has(groupId)) {
        // Show children: interleaved columns and subgroups
        group.children.forEach((childId) => {
          const col = schema.byId.get(childId);
          if (col) {
            entries.push({
              kind: 'column',
              id: col.id,
              name: col.name,
              level: level + 1,
              column: col,
            });
          } else {
            // It's a subgroup
            addGroupEntries(childId, level + 1);
          }
        });
      }
    };

    topLevelItems.forEach((item) => {
      if (item.kind === 'column') {
        const col = schema.byId.get(item.id);
        if (col) {
          entries.push({
            kind: 'column',
            id: col.id,
            name: col.name,
            level: 0,
            column: col,
          });
        }
      } else {
        addGroupEntries(item.id, 0);
      }
    });

    return entries;
  }, [schema, searchText, expandedGroups]);

  const handleShowAll = () => {
    onChange({ ...layout, hidden: [] });
  };

  const handleHideAll = () => {
    const allNonPinned = schema.columns.filter((col) => !layout.pinned.includes(col.id)).map((col) => col.id);
    onChange({ ...layout, hidden: allNonPinned });
  };

  const handleUnpinAll = () => {
    onChange({ ...layout, pinned: [] });
  };

  const handleToggleExpand = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleToggleGroupVisibility = (group: ColumnGroup) => {
    const allHidden = group.columnIds.every((colId) => layout.hidden.includes(colId));
    const newHidden = allHidden
      ? layout.hidden.filter((id) => !group.columnIds.includes(id))
      : [...new Set([...layout.hidden, ...group.columnIds])];
    onChange({ ...layout, hidden: newHidden });
  };

  const handleToggleCollapsed = (groupId: string) => {
    const isCollapsed = layout.collapsedGroups.includes(groupId);
    const newCollapsed = isCollapsed
      ? layout.collapsedGroups.filter((id) => id !== groupId)
      : [...layout.collapsedGroups, groupId];
    onChange({ ...layout, collapsedGroups: newCollapsed });
  };

  const handleToggleColumnVisibility = (columnId: string) => {
    const isHidden = layout.hidden.includes(columnId);
    const newHidden = isHidden
      ? layout.hidden.filter((id) => id !== columnId)
      : [...layout.hidden, columnId];

    // If showing a pinned column, make sure to unpin it if it was hidden
    const newPinned = isHidden && layout.pinned.includes(columnId)
      ? layout.pinned
      : layout.pinned;

    onChange({ ...layout, hidden: newHidden, pinned: newPinned });
  };

  const handleTogglePin = (columnId: string) => {
    const isPinned = layout.pinned.includes(columnId);
    if (isPinned) {
      // Unpin: remove from pinned, ensure it's in order if not there
      const newPinned = layout.pinned.filter((id) => id !== columnId);
      const newOrder = layout.order.includes(columnId) ? layout.order : [...layout.order, columnId];
      onChange({ ...layout, pinned: newPinned, order: newOrder });
    } else {
      // Pin: add to pinned, remove from hidden
      const newPinned = [...layout.pinned, columnId];
      const newHidden = layout.hidden.filter((id) => id !== columnId);
      onChange({ ...layout, pinned: newPinned, hidden: newHidden });
    }
  };

  const handleMoveColumn = (columnId: string, direction: 'up' | 'down') => {
    const idx = layout.order.indexOf(columnId);
    if (idx === -1) return;

    const newOrder = [...layout.order];
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= newOrder.length) return;

    [newOrder[idx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[idx]];
    onChange({ ...layout, order: newOrder });
  };

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: spacing.md }}>
      {/* Search */}
      <Input
        size="sm"
        placeholder="Find columns…"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        leftElement={<Icon name="search" size={14} />}
      />

      {/* Counts and actions */}
      <div
        style={{
          fontSize: fontSize.xs,
          color: mutedColor,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          flexWrap: 'wrap',
        }}
      >
        <span>
          {visibleCount} of {schema.columns.length} columns visible · {pinnedCount} pinned
        </span>
        <Button variant="link" size="sm" onClick={handleShowAll}>
          Show all
        </Button>
        <Button variant="link" size="sm" onClick={handleHideAll}>
          Hide all
        </Button>
        <Button variant="link" size="sm" onClick={handleUnpinAll} disabled={pinnedCount === 0}>
          Unpin all
        </Button>
      </div>

      {/* Tree */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {treeEntries.map((entry) => {
          if (entry.kind === 'group' && entry.group) {
            return <GroupRow
              key={entry.id}
              group={entry.group}
              level={entry.level}
              expanded={expandedGroups.has(entry.id)}
              layout={layout}
              onToggleExpand={handleToggleExpand}
              onToggleVisibility={handleToggleGroupVisibility}
              onToggleCollapsed={handleToggleCollapsed}
              theme={theme}
            />;
          } else if (entry.kind === 'column' && entry.column) {
            return <ColumnRow
              key={entry.id}
              column={entry.column}
              level={entry.level}
              layout={layout}
              summary={summaries.get(entry.id)}
              searching={searchText.trim() !== ''}
              onToggleVisibility={handleToggleColumnVisibility}
              onTogglePin={handleTogglePin}
              onMove={handleMoveColumn}
              onJumpTo={onJumpTo}
              theme={theme}
            />;
          }
          return null;
        })}
      </div>
    </div>
  );
}

interface GroupRowProps {
  group: ColumnGroup;
  level: number;
  expanded: boolean;
  layout: any;
  onToggleExpand: (id: string) => void;
  onToggleVisibility: (group: ColumnGroup) => void;
  onToggleCollapsed: (id: string) => void;
  theme: any;
}

function GroupRow({
  group,
  level,
  expanded,
  layout,
  onToggleExpand,
  onToggleVisibility,
  onToggleCollapsed,
  theme,
}: GroupRowProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  const allHidden = group.columnIds.every((colId) => layout.hidden.includes(colId));
  const someHidden = group.columnIds.some((colId) => layout.hidden.includes(colId));
  const isIndeterminate = !allHidden && someHidden;
  const isCollapsed = layout.collapsedGroups.includes(group.id);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} 0`,
    paddingLeft: `${level * 16}px`,
    fontSize: fontSize.xs,
    minHeight: '26px',
    fontFamily: fontFamily.mono,
  };

  return (
    <div style={rowStyle}>
      <button
        onClick={() => onToggleExpand(group.id)}
        aria-label={expanded ? 'Collapse' : 'Expand'}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: theme.colors.text.secondary,
        }}
      >
        <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
      </button>
      <Checkbox
        ref={checkboxRef}
        checked={!allHidden}
        onChange={() => onToggleVisibility(group)}
        aria-label={`Toggle ${group.name} visibility`}
      />
      <span style={{ flex: 1, color: theme.colors.text.primary }}>
        {group.name} ({group.columnIds.length})
      </span>
      <button
        onClick={() => onToggleCollapsed(group.id)}
        aria-label={isCollapsed ? 'Expand to columns' : 'Collapse to one column'}
        aria-pressed={isCollapsed}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: isCollapsed ? theme.colors.primary[500] : theme.colors.text.secondary,
        }}
      >
        <Icon name={isCollapsed ? 'plus' : 'minus'} size={14} />
      </button>
    </div>
  );
}

interface ColumnRowProps {
  column: ColumnDef;
  level: number;
  layout: any;
  summary: any;
  searching: boolean;
  onToggleVisibility: (id: string) => void;
  onTogglePin: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onJumpTo: (id: string) => void;
  theme: any;
}

function ColumnRow({
  column,
  level,
  layout,
  summary,
  searching,
  onToggleVisibility,
  onTogglePin,
  onMove,
  onJumpTo,
  theme,
}: ColumnRowProps) {
  const isVisible = !layout.hidden.includes(column.id);
  const isPinned = layout.pinned.includes(column.id);

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} 0`,
    paddingLeft: `${level * 16 + (searching ? 0 : 20)}px`,
    fontSize: fontSize.xs,
    minHeight: '26px',
    fontFamily: fontFamily.mono,
  };

  const nameStyle: React.CSSProperties = {
    flex: 1,
    cursor: 'pointer',
    color: theme.colors.primary[500],
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const badgeStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: theme.colors.text.secondary,
    background: theme.colors.neutral[100],
    padding: '1px 4px',
    borderRadius: '3px',
  };

  const mutedTextStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: theme.colors.text.secondary,
  };

  return (
    <div style={rowStyle}>
      <Checkbox
        checked={isVisible}
        onChange={() => onToggleVisibility(column.id)}
        aria-label={`Toggle ${column.id} visibility`}
      />
      <span style={nameStyle} onClick={() => onJumpTo(column.id)} title={column.id}>
        {searching ? column.id : column.name}
      </span>
      <span style={badgeStyle}>{TYPE_BADGE[column.type] || column.type}</span>
      {summary && (
        <span style={mutedTextStyle}>
          {summary.missingPct > 0 ? `${Math.round(summary.missingPct)}%` : ''}
        </span>
      )}
      <button
        onClick={() => onTogglePin(column.id)}
        aria-label={isPinned ? 'Unpin' : 'Pin'}
        aria-pressed={isPinned}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: isPinned ? theme.colors.primary[500] : theme.colors.text.secondary,
        }}
      >
        <Icon name="arrow-left" size={14} />
      </button>
      <button
        onClick={() => onMove(column.id, 'up')}
        aria-label="Move up"
        disabled={layout.order.indexOf(column.id) === 0}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: theme.colors.text.secondary,
          opacity: layout.order.indexOf(column.id) === 0 ? 0.3 : 1,
        }}
      >
        <Icon name="chevron-up" size={14} />
      </button>
      <button
        onClick={() => onMove(column.id, 'down')}
        aria-label="Move down"
        disabled={layout.order.indexOf(column.id) === layout.order.length - 1}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: theme.colors.text.secondary,
          opacity: layout.order.indexOf(column.id) === layout.order.length - 1 ? 0.3 : 1,
        }}
      >
        <Icon name="chevron-down" size={14} />
      </button>
    </div>
  );
}
